/**
 * Panel de navegacion del catalogo: riel de lineas a la izquierda, con su
 * icono, y las categorias de la linea activa a la derecha.
 *
 * Rediseñado el 14-09-2026 tomando el menu de imperial.cl como referencia: el
 * riel va en TRES columnas para que las 37 lineas se vean de una vez, sin
 * desplazar nada, y cada linea lleva un icono (`IconoLinea`) que se reconoce
 * antes de leer el nombre. El panel ocupa el alto que queda bajo la cabecera,
 * al ancho del contenido; eso lo resuelve `Cabecera`, que es quien lo abre.
 *
 * Dos decisiones que importan:
 *
 * 1. Las categorias son enlaces `<a>` de verdad, con el nombre de la categoria
 *    como texto. Es como el robot recorre el catalogo y como el comprador puede
 *    abrir una categoria en otra pestana. Un `div` con `onClick` no seria
 *    navegable ni indexable (SEO-01).
 * 2. El riel usa el patron de pestanas de ARIA: el foco se mueve con las
 *    flechas y cada hoja declara que pestana la controla. Con tres columnas,
 *    arriba/abajo recorre la lista en orden y izquierda/derecha salta a la
 *    linea de al lado.
 *
 * En pantalla angosta no hay "al lado": el menu es un acordeon
 * (`MenuAcordeon`), con las categorias abiertas debajo de su linea.
 */
import { useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';

import type { LineaNavegacion } from '@elcarpintero/compartido';

import { useConsultaMedios } from '../../hooks/use-consulta-medios';
import { Icono } from '../comunes/Icono';
import { IconoLinea } from '../comunes/IconoLinea';
import { MenuAcordeon } from './MenuAcordeon';
import { numero } from '../../utilidades/formato';
import { rutas } from '../../utilidades/rutas';

/**
 * Columnas del riel en escritorio: tiene que calzar con `grid-template-columns`
 * de `.indice-rail`. Bajo 1180px el CSS pasa a dos y el salto lateral queda
 * un poco desfasado; es tolerable, porque con el mouse ni se nota.
 */
const COLUMNAS_RIEL = 3;

/** Desde donde el menu es acordeon; calza con el `@media` de `indice.css`. */
const PANTALLA_ANGOSTA = '(max-width:860px)';

export interface MegaMenuProps {
  lineas: LineaNavegacion[];
  /** Prefijo de los id: el menu vive dos veces en el sitio y no pueden chocar. */
  prefijo: string;
  /** Se llama al elegir una categoria, para que el panel flotante se cierre. */
  alNavegar?: () => void;
}

const SALTOS: Record<string, number> = {
  ArrowDown: 1,
  ArrowUp: -1,
  ArrowRight: COLUMNAS_RIEL,
  ArrowLeft: -COLUMNAS_RIEL,
};

export function MegaMenu({ lineas, prefijo, alNavegar }: MegaMenuProps) {
  const primera = lineas[0];
  const [activa, setActiva] = useState<number | null>(primera?.id ?? null);
  // Con el riel en tres columnas, ir con el puntero desde una linea del medio
  // hasta sus categorias obliga a pasar por encima de otras lineas, y cada una
  // se activaba al pasar: las del medio no se podian elegir. Un clic deja la
  // linea fija y el puntero deja de cambiarla; solo otro clic (o el teclado)
  // la mueve. Se suelta al cerrar el panel, porque `Cabecera` lo vuelve a
  // montar en cada apertura.
  const [fijada, setFijada] = useState(false);
  const angosta = useConsultaMedios(PANTALLA_ANGOSTA);

  if (primera === undefined) return null;
  if (angosta) {
    return <MenuAcordeon lineas={lineas} prefijo={prefijo} {...(alNavegar ? { alNavegar } : {})} />;
  }

  const lineaActiva = lineas.find((linea) => linea.id === activa) ?? primera;

  function mover(evento: KeyboardEvent<HTMLButtonElement>, indice: number): void {
    const salto = SALTOS[evento.key] ?? 0;
    if (salto === 0) return;

    evento.preventDefault();
    const siguiente = lineas[(indice + salto + lineas.length) % lineas.length];
    if (siguiente !== undefined) {
      setActiva(siguiente.id);
      document.getElementById(`${prefijo}-linea-${siguiente.id}`)?.focus();
    }
  }

  return (
    <div className="indice">
      <div className="indice-rail" role="tablist" aria-label="Líneas de producto" aria-orientation="vertical">
        {lineas.map((linea, indice) => {
          const seleccionada = linea.id === lineaActiva.id;
          return (
            <button
              key={linea.id}
              type="button"
              role="tab"
              id={`${prefijo}-linea-${linea.id}`}
              className={seleccionada ? 'indice-linea activo' : 'indice-linea'}
              aria-selected={seleccionada}
              aria-controls={`${prefijo}-hoja-${linea.id}`}
              tabIndex={seleccionada ? 0 : -1}
              onClick={() => {
                setActiva(linea.id);
                setFijada(true);
              }}
              onMouseEnter={() => {
                if (!fijada) setActiva(linea.id);
              }}
              onKeyDown={(evento) => mover(evento, indice)}
            >
              <IconoLinea slug={linea.slug} className="indice-icono" />
              <span>{linea.nombre}</span>
              <span className="cuenta">{numero(linea.cantidadProductos)}</span>
            </button>
          );
        })}
      </div>

      <div className="indice-panel">
        <section
          className="indice-hoja activo"
          id={`${prefijo}-hoja-${lineaActiva.id}`}
          role="tabpanel"
          aria-labelledby={`${prefijo}-linea-${lineaActiva.id}`}
          tabIndex={0}
        >
          <header className="indice-cab">
            <div className="indice-cab-nombre">
              <IconoLinea slug={lineaActiva.slug} className="indice-cab-icono" />
              <div>
                {/* Rotulo, no encabezado: el panel vive en la cabecera y un h2
                    antes del h1 de la pagina rompe la jerarquia de la vista. */}
                <p className="t">{lineaActiva.nombre}</p>
                <span className="d">
                  {numero(lineaActiva.categorias.length)} categorias ·{' '}
                  {numero(lineaActiva.cantidadProductos)} productos
                </span>
              </div>
            </div>
            <Link className="indice-todo" to={rutas.linea(lineaActiva.slug)} onClick={alNavegar}>
              Ver la línea completa
              <Icono nombre="flecha" />
            </Link>
          </header>

          <div className="indice-cols">
            {lineaActiva.categorias.map((categoria) => (
              <Link
                key={categoria.id}
                className="indice-cat"
                to={rutas.categoria(categoria.slug)}
                onClick={alNavegar}
              >
                <span>{categoria.nombre}</span>
                <span className="n">{numero(categoria.cantidadProductos)}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
